/**
 * Hook to get the current organization consistently across all components.
 * Persists the user's organization selection in normal sessions, while using
 * sessionStorage for tab-scoped impersonation sessions.
 */

import { getScopedStorageItem } from "@/lib/session-utils";

interface Organization {
  id: string;
  name: string;
  role: string;
  isPersonal?: boolean;
}

interface CurrentOrganizationResult {
  currentOrganizationId: string | undefined;
  currentOrg: Organization | undefined;
  isPersonalOrg: boolean;
}

export function useCurrentOrganization(organizations: Organization[]): CurrentOrganizationResult {
  const storedOrgId = getScopedStorageItem('currentOrganizationId');
  
  // Use stored ID if valid, otherwise fallback to first organization
  const currentOrganizationId = storedOrgId && organizations.some(org => org.id === storedOrgId)
    ? storedOrgId
    : organizations[0]?.id;
  
  // Find the current organization object
  const currentOrg = organizations.find(org => org.id === currentOrganizationId) || organizations[0];
  
  // Check if it's a personal organization
  const isPersonalOrg = currentOrg?.isPersonal === true;
  
  return { currentOrganizationId, currentOrg, isPersonalOrg };
}

export default useCurrentOrganization;
